import React, { useEffect, useCallback } from 'react';
import { X, CheckCircle2, RefreshCcw, ChevronRight, Maximize2 } from 'lucide-react';

/**
 * StepVisualModal — Modal plein écran affichant le SVG pédagogique d'une étape
 * de progression + boutons de validation rapide.
 *
 * Props :
 *  - `open` (bool) — visibilité
 *  - `onClose` (fn) — fermeture
 *  - `step` ({ id, label, remc, phase, phaseSlug, phaseColor }) — étape courante
 *  - `svgUrl` (string) — URL du SVG à afficher
 *  - `currentStatus` (string|null) — 'todo' | 'en_cours' | 'a_revoir' | 'acquis'
 *  - `onStatus` (fn(status)) — callback de validation
 */
export default function StepVisualModal({ open, onClose, step, svgUrl, currentStatus, onStatus }) {
  const handleKey = useCallback((e) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    if (!open) return undefined;
    document.addEventListener('keydown', handleKey);
    // Bloquer le scroll body pendant que la modal est ouverte
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, handleKey]);

  if (!open || !step) return null;

  const doStatus = (st) => {
    onStatus?.(st);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[110] flex flex-col p-2 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="step-visual-title"
      data-testid="step-visual-modal"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/85 backdrop-blur-md" onClick={onClose} />

      {/* Contenu */}
      <div className="relative flex-1 flex flex-col rounded-2xl bg-[#0d0d0e] border border-white/10 overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3 border-b border-white/10 bg-black/40">
          <div className="min-w-0">
            <div className="text-red-300 font-mono-spec text-[10.5px] uppercase tracking-wider font-bold flex items-center gap-2">
              <span>Étape {step.id}</span>
              {step.remc && <span className="text-white/40">·</span>}
              {step.remc && <span className="text-white/60">REMC {step.remc}</span>}
              {step.phase && <span className="text-white/40">·</span>}
              {step.phase && <span className="text-white/50">{step.phase}</span>}
            </div>
            <div id="step-visual-title" className="mt-0.5 font-serif-editorial text-white text-[16px] sm:text-[18px] leading-tight truncate">
              {step.label}
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {svgUrl && typeof svgUrl === 'string' && (
              <a
                href={svgUrl}
                target="_blank"
                rel="noopener noreferrer"
                title="Ouvrir dans un nouvel onglet"
                aria-label="Ouvrir le visuel dans un nouvel onglet"
                className="h-9 w-9 flex items-center justify-center rounded-full border border-white/15 text-white/70 hover:text-white hover:bg-white/5"
                data-testid="step-visual-fullscreen"
              >
                <Maximize2 size={15} />
              </a>
            )}
            <button
              onClick={onClose}
              data-testid="step-visual-close"
              aria-label="Fermer le visuel"
              className="h-9 w-9 flex items-center justify-center rounded-full border border-white/15 text-white/70 hover:text-white hover:bg-white/5"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Corps : composant pilote qualité pro */}
        <div className="flex-1 overflow-auto bg-white/[0.02] p-3 sm:p-5 flex items-start justify-center">
          {svgUrl && typeof svgUrl === 'object' && svgUrl.component ? (
            // 🎨 PILOTE QUALITÉ : composant React dédié
            <div className="w-full max-w-6xl">
              <div className="mb-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-red-500/50 bg-red-500/10 text-red-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">
                ⭐ Pilote qualité pro · {svgUrl.label || 'Visuel interactif'}
              </div>
              <svgUrl.component />
            </div>
          ) : (
            <div className="text-white/50 text-[13.5px] text-center p-8">
              Aucun visuel pédagogique n&apos;est disponible pour cette étape.
            </div>
          )}
        </div>

        {/* Footer : validation */}
        <div className="border-t border-white/10 bg-black/40 p-3 sm:p-4">
          <div className="text-white/50 text-[11px] font-mono-spec uppercase tracking-wider mb-2.5">
            Valide cette étape maintenant
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <button
              onClick={() => doStatus('a_revoir')}
              data-testid="step-visual-validate-a_revoir"
              className={`h-11 rounded-full text-[12.5px] font-bold inline-flex items-center justify-center gap-1.5 transition ${
                currentStatus === 'a_revoir'
                  ? 'bg-red-500 text-red-950 shadow-lg shadow-red-950/40'
                  : 'border border-red-500/40 bg-red-950/25 text-red-200 hover:bg-red-500/20'
              }`}
            >
              <RefreshCcw size={13} /> À revoir
            </button>
            <button
              onClick={() => doStatus('en_cours')}
              data-testid="step-visual-validate-en_cours"
              className={`h-11 rounded-full text-[12.5px] font-bold inline-flex items-center justify-center gap-1.5 transition ${
                currentStatus === 'en_cours'
                  ? 'bg-amber-500 text-amber-950 shadow-lg shadow-amber-950/40'
                  : 'border border-amber-500/40 bg-amber-950/25 text-amber-200 hover:bg-amber-500/20'
              }`}
            >
              <ChevronRight size={13} /> En cours
            </button>
            <button
              onClick={() => doStatus('acquis')}
              data-testid="step-visual-validate-acquis"
              className={`h-11 rounded-full text-[12.5px] font-bold inline-flex items-center justify-center gap-1.5 transition col-span-2 sm:col-span-1 ${
                currentStatus === 'acquis'
                  ? 'bg-emerald-500 text-emerald-950 shadow-lg shadow-emerald-950/40'
                  : 'border border-emerald-500/50 bg-emerald-950/30 text-emerald-200 hover:bg-emerald-500/25'
              }`}
            >
              <CheckCircle2 size={13} /> Acquis
            </button>
            <button
              onClick={onClose}
              data-testid="step-visual-cancel"
              className="h-11 rounded-full text-[12.5px] font-semibold border border-white/15 text-white/70 hover:bg-white/5 inline-flex items-center justify-center gap-1.5"
            >
              Fermer sans valider
            </button>
          </div>
          <div className="mt-2.5 text-white/45 text-[11px] leading-relaxed">
            Astuce : valider en <span className="text-emerald-300 font-semibold">Acquis</span> déclenche un effet visuel de célébration 🎉
          </div>
        </div>
      </div>
    </div>
  );
}
